import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { useTranslation } from "react-i18next";
import { haatSignLogoIc } from "../../assets";
import { APP_VERSION } from "../../shared/constants/appInfo";
import {
  getLocalizedPageTitle,
  getLocalizedSectionTitle,
  groupResourcePagesBySection,
} from "../resources/resourceRegistry";
import "../styles/sidebar.css";

const Sidebar = ({ items, selectedSlug, onSelect }) => {
  const { t } = useTranslation();
  const visibleItems = items || [];
  const visibleGroups = groupResourcePagesBySection(visibleItems);
  const selectedItemRef = useRef(null);
  const selectedGroupKey = useMemo(
    () =>
      visibleGroups.find((group) =>
        group.items.some((item) => item.slug === selectedSlug),
      )?.key || null,
    [selectedSlug, visibleGroups],
  );
  const [openGroups, setOpenGroups] = useState(() =>
    selectedGroupKey ? { [selectedGroupKey]: true } : {},
  );

  useEffect(() => {
    if (!selectedGroupKey) {
      return;
    }

    setOpenGroups((current) => {
      if (current[selectedGroupKey]) {
        return current;
      }

      return {
        ...current,
        [selectedGroupKey]: true,
      };
    });
  }, [selectedGroupKey]);

  const isSelectedGroupOpen = selectedGroupKey
    ? Boolean(openGroups[selectedGroupKey])
    : false;

  useEffect(() => {
    const target = selectedItemRef.current;

    if (!target) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [selectedSlug, visibleItems.length, isSelectedGroupOpen]);

  const toggleGroup = (groupKey) => {
    setOpenGroups((current) => ({
      ...current,
      [groupKey]: !current[groupKey],
    }));
  };

  return (
    <nav className="sidebar" aria-label={t("sidebar.ariaLabel")}>
      <div className="sidebar-inner">
        <div className="sidebar-logo-wrap">
          <Link to="/resources" aria-label="Resources">
            <img
              src={haatSignLogoIc}
              alt="Haatsign logo"
              className="sidebar-logo"
            />
          </Link>
        </div>
        <p className="sidebar-version" dir="ltr">
          {APP_VERSION}
        </p>

        <SimpleBar style={{ flex: 1, minHeight: 0 }} autoHide={false}>
          <div className="sidebar-groups">
            {visibleGroups.length === 0 ? (
              <p className="sidebar-empty">{t("sidebar.noResults")}</p>
            ) : (
              visibleGroups.map((group) => {
                const isOpen = Boolean(openGroups[group.key]);
                const groupListId = `sidebar-group-${group.key}`;

                return (
                  <section key={group.key} className="sidebar-group">
                    <button
                      type="button"
                      className="sidebar-group-toggle"
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={isOpen}
                      aria-controls={groupListId}
                    >
                      <span className="sidebar-group-title">
                        {getLocalizedSectionTitle(group, t)}
                      </span>
                      <span
                        className={`sidebar-group-caret${
                          isOpen ? " open" : ""
                        }`}
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path d="m9 6 6 6-6 6" />
                        </svg>
                      </span>
                    </button>

                    {isOpen && (
                      <ul id={groupListId} className="sidebar-link-list">
                        {group.items.map((item) => (
                          <li key={item.slug}>
                            <button
                              type="button"
                              className={`sidebar-link ${
                                selectedSlug === item.slug ? "selected" : ""
                              }`}
                              ref={
                                selectedSlug === item.slug
                                  ? selectedItemRef
                                  : null
                              }
                              onClick={() => onSelect(item.slug)}
                            >
                              {getLocalizedPageTitle(item, t)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })
            )}
          </div>
        </SimpleBar>
      </div>
    </nav>
  );
};

export default Sidebar;
